import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import {
    gql, useLazyQuery, useQuery, useReactiveVar, useSuspenseQuery,
} from '@apollo/client';

import FILTER_QUERY from 'app/graphql/network/filter';
import { useSelectedFilters } from 'app/connect/connectToSelectedFilters';

import {
    paymentMethodVar, regionSubdivisionVar, selectedPeriodVar, selectedTagsVar,
    subscriptionTypeVar,
} from 'app/apollo/reaction';
import { sleep, toUTS, URLUpdater } from 'global/utils';
import date, { isDateInRange } from 'app/utils/date';
import { useMenuPeriodsData } from 'app/hooks/accessors';
import { useUDDData } from 'app/hooks/accessors/useUDDData';
import { MenuSkeletonPage } from 'app/components/Skeletons/MenuPageSkeletons';
import { _getIsRecurrentHiddenByPeriod } from 'app/views/Checkout/checkout.utils';
import { SUBSCRIPTION_TYPES } from 'app/const/subscription';
import { getPreAssembledType, usePreAssembledBasketType } from './usePreAssembledBasketType';

export const FiltersContext = createContext({
    filterQuery: {},
    selectedFilters: {
        isSelectedDefaultFilters: false,
        isUserChangesTags: false,
        prevSelectedPeriods: [],
        selectedCategories: [],
        selectedCategoryId: '',
        selectedPeriod: '',
        selectedTags: [],
    },
});

/**
 * @description Проверят можно находится на этом урле в скрытом периоде
 * @returns boolean
 */
const isHiddenPeriodPossible = () => {
    const { pathname } = window.location;
    const regex = /^\/(menu|profile|app)(\/.*)?$/;
    return !regex.test(pathname);
};
/**
    утилита, чтобы выставить первый период дефолтно, даже если firstPeriodStart в конфиге не учитывает регион
    вынесена в app/utils/date, чтобы ее можно было использовать без тяжелых зависимостей контекста
*/
export { isDateInRange };
/**
 * @description тут ставиться переменная period при инициализации приложения VII
 */
export const getInitialPeriodValue = (periods, regionSubdivision) => {
    const urlParams = new URLSearchParams(window.location.search);
    const periodQueryParam = urlParams.get('period');
    const periodFromUrl = periods?.find(
        (p) => (p.start === periodQueryParam || isDateInRange(p.start, periodQueryParam)) && (p.isVisibleInFilter === 'visible'),
    ) || {};
    const firstVisablePeriod = periods?.find((p) => p.isVisibleInFilter === 'visible') || {};

    return periodFromUrl?.start || firstVisablePeriod?.start;
};

/**
 * @description Ставит query парамерт period
 */
export const setInitialPeriodAsQueryParam = (initialPeriod) => {
    URLUpdater({ period: initialPeriod });
};

/**
 * @description Инициализирует period. Ставит его в URL и d реактивную переменную
 * @param menuFilter из query MenuFilter
 */
export const initPeriodInApp = (menuFilter, period, patchedPeriods = []) => {
    const isMenuFilter = 'periods' in menuFilter;
    const hasPatchedClientPeriods = Boolean(patchedPeriods.length);

    const periods = hasPatchedClientPeriods
        ? patchedPeriods
        : menuFilter.periods;

    const initialPeriod = isMenuFilter
        ? getInitialPeriodValue(periods)
        : period;


    setInitialPeriodAsQueryParam(initialPeriod);
    selectedPeriodVar(initialPeriod);
};
/**
 *
 * @param {*} param0
 * @returns
 */
export const setAnoterPeriodHandler = (period, withRouter = true) => {
    if (withRouter) {
        URLUpdater({ period });
    }
    selectedPeriodVar(period);
};

// filter_tag=1219.1218

const DataReadyWrapper = ({
    /* eslint-disable react/prop-types */
    datesLoading,
    children,
    /* eslint-enable */
}) => {
    /* FALLBACK */
    if (!datesLoading) return <MenuSkeletonPage />;
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
};

/*
  /basket/
    ?cm=10
    &date=2025-08-18
    &hide=payment_button_recurrent
    &menu=everyday
    &payment=online
    &period=2025-08-11
    &promo_code=OSN42
    &utm_campaign=activation
    &utm_medium=crm
    &utm_source=tb
*/

/*
    basket/?dishes=4716.3785&menu=top

*/

const updateHideParam = (value) => {
    const urlParams = new URLSearchParams(window.location.search);
    const hideQueryParam = urlParams.get('hide') || value || '';
    const nextHideString = [...new Set([...hideQueryParam.split('.'), value])].join('.');
    URLUpdater({ hide: nextHideString });
};

export const removeFromHideParam = (value) => {
    const urlParams = new URLSearchParams(window.location.search);
    const hideQueryParam = urlParams.get('hide') || value || '';

    const nextHideString = [
        ...new Set([...hideQueryParam.split('.')].filter((itm) => itm !== value)),
    ].join('.');

    URLUpdater({ hide: nextHideString });
};

const setInitilaFilterTagsFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('filter_tag')) {
        const tagsFromURL = urlParams.get('filter_tag');
        const tagsList = tagsFromURL.split('.');
        selectedTagsVar(tagsList);
    }
};

const getUDDFieldAccessorBySetType = (type) => {
    switch (type) {
        case 'top':
            return 'topAvailableDates';
        case 'hiddenTop':
            return 'topAvailableDates';
        case 'premium':
            return 'premiumAvailableDates';
        case 'every':
            return 'everydayAvailableDates';
        case 'hiddenPremium':
            return 'premiumAvailableDates';
        default:
            return 'eeAvailableDates';
    }
};

const E48 = (text) => `\x1b[46;30;1m ${text} \x1b[0m`;

const ResolvePeriodDataWrapper = ({
    /* eslint-disable react/prop-types */
    children,
    /* eslint-enable */
}) => {
    const preAssembledType = usePreAssembledBasketType();
    const regionSubdivision = useReactiveVar(regionSubdivisionVar);
    const selectedPeriod = useReactiveVar(selectedPeriodVar);

    const [ready, setReady] = useState(false);

    const { periods } = useMenuPeriodsData();
    const { dates } = useUDDData();

    /* SELECTORS */
    const isSetUrlContext = useMemo(() => {
        const type = getPreAssembledType(window.location.search);
        const setsAvailableInHiddenPeriod = ['every', 'top', 'premium', 'hiddenPremium', 'hiddenTop'];
        return isHiddenPeriodPossible() && setsAvailableInHiddenPeriod.includes(type);
    }, []);

    const eePeriod = useMemo(
        () => getInitialPeriodValue(periods, regionSubdivision),
        [periods, regionSubdivision],
    );

    const setPeriod = useMemo(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const periodQueryParam = urlParams.get('period');

        const type = getPreAssembledType(window.location.search);
        const field = getUDDFieldAccessorBySetType(type);

        const setPeriodFromUrl = dates.find((itm) => (
            itm[field]?.length > 0 && (itm.startDate === periodQueryParam
                || isDateInRange(periodQueryParam, itm.startDate)
            )
        ),
        );
        const firstSetPeriod = dates.find((itm) => itm[field].length > 0);

        return setPeriodFromUrl?.startDate || firstSetPeriod?.startDate || eePeriod;
    }, []);


    /* EFFECTS */
    useEffect(() => {
        setInitilaFilterTagsFromURL();
        // const type = getPreAssembledType(window.location.search);

        // console.log(E48('DEV'), 'Set context', isSetUrlContext, type);
        // console.log(E48('DEV'), setPeriod, dates);

        if (isSetUrlContext) {
            setAnoterPeriodHandler(setPeriod);
            const period = periods.find((itm) => itm?.start === setPeriod || isDateInRange(setPeriod, itm.start));

            if (period.isVisibleInFilter === 'hidden') {
                updateHideParam('edit_basket');
            }
        } else {
            setAnoterPeriodHandler(eePeriod);
        }

        setReady(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // FALLBACK
    if (!ready) return <>крутилка</>;
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
};

export const FiltersContextProvider = (props) => {
    const { children, setAppReady, appReady } = props;

    /* HOOKS */
    const selectedFilters = useSelectedFilters();
    const regionSubdivision = useReactiveVar(regionSubdivisionVar);
    const setName = usePreAssembledBasketType();
    const preAssembledType = usePreAssembledBasketType();

    /* DA */
    const [fetchUDDAndFilterDates, { data, loading: datesLoading }] = useLazyQuery(
        gql`query ROOT_UDD_AND_FILTER_DATES_QUERY(
            $subdivision: countrySubdivision!
        ) {
            menuDates(subdivision: $subdivision) {
                dates {
                    startDate
                    eeAvailableDates
                    everydayAvailableDates
                    trialAvailableDates
                    premiumAvailableDates
                    topAvailableDates
                    zapasAvailableDates

                    setsAvailableDates @client
                    allAvailableDates @client
                }
                subdivision
            }
            menuSidebar(subdivision: $subdivision) {
                periods {
                    start
                    end
                    isVisibleInFilter
                    categories {
                        id
                        title
                        description
                    }
                    tags {
                        id
                        title
                        categoryId: category_id
                        categoryName: category_name
                        erp_id
                    }
                }
                subdivision
            }
        }
    `, {
            variables: {
                subdivision: regionSubdivision,
            },
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'cache-only',
            context: {
                message: 'root:init:FiltersContextProvider',
            },
        },
    );

    useEffect(() => {
        async function fetchData() {
            await fetchUDDAndFilterDates();
            // await sleep(10000);
            setAppReady((state) => ({
                ...state,
                FiltersContextProvider: true,
            }));
        }
        fetchData();
    }, []);

    const contextValue = useMemo(() => {
        const value = {
            selectedFilters,
        };

        return value;
    }, [selectedFilters]);

    return (
        <DataReadyWrapper
            datesLoading={appReady.FiltersContextProvider}
        >
            <ResolvePeriodDataWrapper>
                <FiltersContext.Provider value={contextValue}>
                    {children}
                </FiltersContext.Provider>
            </ResolvePeriodDataWrapper>
        </DataReadyWrapper>
    );
};

FiltersContextProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};

export const withFiltersContext = (WrappedComponent) => (forwardedProps) => {
    const { filterQuery } = useContext(FiltersContext);

    return (
        <WrappedComponent
            filterQuery={filterQuery}
            {...forwardedProps}
        />
    );
};

export const clearAfterSwitchDialogTexts = {
    ru: {
        strongText: 'Нужно освободить корзину,',
        regularText: 'чтобы переключить дату',
        confirmText: 'Хорошо',
        rejectText: 'Нет, не надо',
    },
    en: {
        strongText: 'Empty your basket',
        regularText: 'to switch period',
        confirmText: 'OK',
        rejectText: 'No, go back',
    },
};


/*
        FILTER CONTEXT
        0.5. Нужна реактивная переменная которая ставит subdivision, c ней нужно вызывать EE_DATES
        1. Если default и он скрыт, то первый visible
        2. Если сет смотрим УДД, если в УДД есть даты то ставим периуд из УДД

        BASKET
        1. Бэк должен отдавать every на скрытый периуд
        2. Если мы пытаемся изменить корзину, то ставим первый visible периуд


НУЖНО ОПЕРДЕЛИТЬ СЕТЫ, И НА ВСЕ МУТАЦИИ ADD TO BASKET нужно убрать из игру query параметры

// EVERY
?cm=10&menu=everyday&payment=recurrent

// TOP 5
?cm=10&date=2025-07-31&payment=recurrent&period=2025-07-28&promo_code=22try5x2

// GREEN
?cm=10&extra=green&menu=premium

cm=10 === set
menu=everyday | premium
extra=green

cm=10 & menu=mix TOP 5

cm=10 & menu=everyday EVERY

cm=10 & menu=premium & extra=green GREEN


1. Определить по урлу что у нас в нем
2. При взаимоде с add to basket убрать из урла параметры
3. Нужно сапоставить EE DATES с сетом
4. Нужно логика работы с периудом для сетов


*/


// const filterQuery = useQuery(gql`
//     query ROOT_FILTER_QUERY {

//     }
// `, {
//     fetchPolicy: 'network-only',
//     nextFetchPolicy: 'standby',
//     context: {
//         message: 'root:init:FiltersContextProvider',
//     },
// });

// /* SELECTORS */
// const handleChoosePeriod = useCallback(
//     (menuFilter) => {
//         // const isSet = availableSetsInHiddenPeriods.includes(setName);
//         // if (isSet && firstPeriodWithEverydayAvailableDates) {
//         //     console.log('old period');
//         //     setInitialPeriodAsQueryParam(firstPeriodWithEverydayAvailableDates);
//         //     selectedPeriodVar(firstPeriodWithEverydayAvailableDates);
//         // } else {
//         //     console.log('new period');
//         // }
//         // setPeriods(menuFilter.periods);
//         initPeriodInApp(menuFilter, null);
//     },
//     [setName],
// );

/* EFFECTS */
// useEffect(
//     () => {
//         // console.log(filterQuery);
//         handleChoosePeriod(filterQuery?.data?.menuFilter);
//     },
//     [],
// );

// useEffect(() => {
//     console.log(regionSubdivision, preAssembledType);
// }, [regionSubdivision, preAssembledType]);

